// src/components/charts/NewAreaChart.jsx
import React from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList
} from "recharts";
import { formatValue } from '@/lib/post_analysis_utils/formatValue';

export default function NewAreaChart({ data, XAxisMetrics, YAxisMetrics }) {
  const { label: yLabel, metric: yMetric, type } = YAxisMetrics;

  // Get the proper metric display name and formatter
  const metricConfig = {
    label: yLabel || yMetric.charAt(0).toUpperCase() + yMetric.slice(1),
    format: (val) => {
      // Use the type from YAxisMetrics or fallback to data type
      const metricType = type || data[0]?.type || 'number';
      return formatValue(val, { type: metricType });
    }
  };

  if (!data || !data.length) {
    return (
      <div className="flex items-center justify-center h-[550px] bg-gray-50 rounded-lg">
        <p className="text-gray-500">No data available for the selected criteria</p>
      </div>
    );
  }

  // Calculate Y-axis upper bound
  const maxVal = Math.max(...data.map(d => d.value), 0);
  const upper = maxVal * 1.1;

  return (
    <div data-testid="summary-area-chart">
      <ResponsiveContainer width="100%" height={550}>
        <AreaChart data={data} margin={{ top: 20, right: 30, left: 60, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            angle={-35}
            textAnchor="end"
            interval={0}
            height={60}
            label={{
              value: XAxisMetrics.label || XAxisMetrics.metric,
              position: "insideBottom",
              offset: -40,
              fontWeight: "bold",
              fontSize: 14,
            }}
          />
          <YAxis
            domain={[0, upper]}
            tickFormatter={metricConfig.format}
            label={{
              value: metricConfig.label,
              angle: -90,
              position: "insideLeft",
              fontWeight: "bold",
              fontSize: 14,
              offset: -40
            }}
          />
          <Tooltip 
            formatter={(value) => [metricConfig.format(value), metricConfig.label]}
            labelFormatter={(label) => `${XAxisMetrics.label || XAxisMetrics.metric}: ${label}`}
          />
          <Area 
            type="monotone" 
            dataKey="value" 
            stroke="#3B82F6" 
            fill="#DBEAFE"
            name={metricConfig.label}
          >
            <LabelList 
              dataKey="value" 
              position="top" 
              formatter={metricConfig.format} 
            />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
